import * as React from 'react';
import { Query } from 'react-apollo';
import gql from 'graphql-tag';
import glamorous from 'glamorous';
import { RvLoader } from '../../components/Loader';
import { SearchCriteria } from '../SearchCriteria';
import CloseIcon from '@material-ui/icons/Close';
import { ChartCommonProps, IconButtonWrapper, ChartCommonState, FooterDescription, HelpWrapper } from '../Common';
import { formatNumber, getMaxTickLimit, getSuggestedMaxTick } from '../../helpers/ChartHelper';
import { WithStyles, withStyles, IconButton } from '@material-ui/core';
import { styles } from '../styles';
import { ChartFilter } from '../ChartComponents';
import { SelectedItemProps } from '../MattersByStatusChart';
import { DrillDownType } from '../DrillDownType';

import { Bar, getElementAtEvent } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, Scale, Tick, Chart, ChartEvent, ActiveElement } from 'chart.js';
import zoomPlugin from 'chartjs-plugin-zoom';
import { ChartInfo } from '../../components/ChartInfo';

import { RV_CHART_CLASS, RV_CHART_COLORS, isChartDataEmpty, rvBarDatasetStyle, rvChartAnimation, rvCategoryAxisStyle, rvTooltipStyle, rvValueAxisStyle } from '../chartTheme';
import { ChartHeading } from '../ChartHeading';
import { ChartStatTiles } from '../ChartStatTiles';
import { AnimatedNumber } from '../AnimatedNumber';
import { ChartSkeleton } from '../../components/loading/ChartSkeleton';
import { ChartEmptyState } from '../ChartEmptyState';
import { ChartNotes } from '../ChartNotes';
import AssessmentOutlinedIcon from '@material-ui/icons/AssessmentOutlined';
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, zoomPlugin);

const BarWrapper = glamorous.div<{ theme?: object }>((props) => ({
    position: 'relative',
    paddingBottom: '10px',
    flex: 1,
    display: 'flex',
    flexFlow: 'column',
    maxWidth: '100%',
    maxHeight: '100%',
    height: '100%',
    '@media print': {
        display: 'flex',
        flexFlow: 'column',
        alignItems: 'center',
    },
}));

const BarTitleContainer = glamorous.div<{ theme?: object }>((props) => ({
    display: 'flex',
    flexFlow: 'column',
    alignItems: 'center',
    padding: '15px 15px 0px 15px',
    fontSize: '16px',
    fontWeight: 600,
    [`& .${`h1`}`]: {
        display: 'flex',
        alignItems: 'center',
    },
    [`& .${`h2`}`]: {
        fontSize: '2.5rem',
        padding: '10px 0',
    },
})).withProps({ className: 'rv-chart-title-area' });

const BarContainer = glamorous.div<{ theme?: object }>((props) => ({
    padding: '5px 15px 15px 15px',
    minHeight: '300px',
    flex: 1,
    display: 'flex',
    flexFlow: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    maxHeight: '350px',
    maxWidth: 'auto',
    position: 'relative',
    '@media (max-width: 533px)': {
        maxWidth: '350px',
        margin: '0 auto',
    },
    [`& .${`titleWrapper`}`]: {
        textAlign: 'center',
        display: 'inline-block',
        width: '100%',
        padding: '15px',
        [`& .${`title`}`]: {
            fontSize: '18px',
            fontWeight: 600,
            display: 'block',
            marginBottom: '15px',
        },
        [`& .${`value`}`]: {
            fontSize: '24px',
            fontWeight: 600,
            display: 'block',
            marginBottom: '15px',
        },
        [`& .${`secondary-title`}`]: {
            fontSize: '16px',
            fontWeight: 400,
            display: 'block',
            marginBottom: '15px',
        },
    },
}));

const LoadWrapper = glamorous.div<{ theme?: object }>((props) => ({
    padding: '20px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
}));

const TotalDiv = glamorous.div<{ theme?: object }>((props) => ({
    cursor: 'pointer',
    paddingRight: 5,
}));

interface ChartProps extends ChartCommonProps, SelectedItemProps, WithStyles<typeof styles> {
    chartFilter?: ChartFilter;
}

interface ChartState extends ChartCommonState {
    //
}

class BilledVsEstimateChart extends React.Component<ChartProps, ChartState> {
    // tslint:disable-next-line: no-any
    public chartRef: React.RefObject<any>;

    constructor(props: ChartProps, state: ChartState) {
        super(props, state);
        this.onCloseChart = this.onCloseChart.bind(this);
        this.handlePopoverOpen = this.handlePopoverOpen.bind(this);
        this.handlePopoverClose = this.handlePopoverClose.bind(this);
        this.handleTotalClick = this.handleTotalClick.bind(this);
        this.onHover = this.onHover.bind(this);

        this.onPan = this.onPan.bind(this);

        this.chartRef = React.createRef();

        this.state = {
            open: false,
            anchorEl: undefined,
        };
    }

    public componentDidMount() {
        //
    }

    public componentWillUnmount() {
        //
    }

    public shouldComponentUpdate(nextProps: ChartProps, nextState: ChartState): boolean {
        if (this.props !== nextProps || this.state !== nextState) {
            return true;
        }

        return false;
    }

    public render() {
        // const { classes } = this.props;
        // tslint:disable-next-line:no-any
        let symbol: any;
        return (
            <React.Fragment>
                {/* tslint:disable-next-line: no-any */}
                <Query<any> query={CurrencySymbol}>
                    {(result) => {
                        if (result.loading) {
                            return (
                                <ChartSkeleton variant="bars" />
                            );
                        }

                        const currencySymbol = result.loading || result.error ? null : result.data.options.currencySymbol;

                        symbol = currencySymbol ? currencySymbol.symbol : null;
                        return (
                            // tslint:disable-next-line: no-any
                            <Query<any>
                                query={ChartData}
                                variables={{
                                    entityType: this.props.chartFilter?.entityType,
                                    entityGuid: this.props.chartFilter?.entityGuid,
                                    responsibility: this.props.chartFilter?.responsibility,
                                }}
                            >
                                {({ loading, error, data }) => {
                                    if (loading) {
                                        return (
                                            <LoadWrapper className="load-wrapper">
                                                <RvLoader size="small" />
                                            </LoadWrapper>
                                        );
                                    }

                                    const statistics = data.statistics;

                                    let matter = loading || error || statistics === null || statistics === undefined ? null : statistics.matter;

                                    let matterBilledFeeAndEstimate = matter ? matter.matterBilledFeeAndEstimate : null;

                                    if (matterBilledFeeAndEstimate) {
                                        const { billedFee, estimate, mattersCount, mattersWithNoEstimate, mattersWithNoEstimateBilledFees } = matterBilledFeeAndEstimate;

                                        let percentage = Math.round((billedFee / estimate) * 100);

                                        if (isNaN(percentage)) {
                                            percentage = 0;
                                        }

                                        return (
                                            <BarWrapper className={RV_CHART_CLASS}>
                                                <IconButtonWrapper aria-label="close">
                                                     <HelpWrapper className="rv-chart-help">
                                                        <ChartInfo 
                                                            description={this.props.description}
                                                        />
                                                    </HelpWrapper>
                                                    <IconButton aria-label="close" onClick={this.onCloseChart} size="small">
                                                        <CloseIcon />
                                                    </IconButton>
                                                </IconButtonWrapper>
                                                <BarTitleContainer>
                                                    <ChartHeading icon={<AssessmentOutlinedIcon />} title="Estimate vs Billed Fees" />
                                                    <TotalDiv className="h2" onClick={() => this.handleTotalClick(true)}>
                                                        <AnimatedNumber value={percentage} format={(amount) => `${Math.round(amount)}%`} />
                                                    </TotalDiv>
                                                    <div className="h3">
                                                        <TotalDiv onClick={() => this.handleTotalClick(false)}>{formatNumber(mattersCount)}</TotalDiv>
                                                        Current Matters with Estimates
                                                    </div>
                                                </BarTitleContainer>
                                                <BarContainer className="rv-chart-bars">
                                                    <Bar
                                                        ref={this.chartRef}
                                                        // tslint:disable-next-line: no-any
                                                        onClick={(event: React.MouseEvent<any>) => {
                                                            const _data = getElementAtEvent(this.chartRef.current, event);
                                                            if (_data.length > 0 && this.props.getSelectedItem) {
                                                                const _index = getElementAtEvent(this.chartRef.current, event)[0].index;

                                                                const item = ['Estimate Fees', 'Billed Time & Fees'][_index];

                                                                const searchCriterias = new Array<SearchCriteria>();

                                                                searchCriterias.push({
                                                                    property: item === 'Estimate Fees' ? 'EstimatedCosts' : 'FeesBilled',
                                                                    operator: 'gt',
                                                                    comparison: '0',
                                                                });

                                                                this.props.getSelectedItem(
                                                                    searchCriterias,
                                                                    false,
                                                                    'Estimate vs Billed Fees',
                                                                    DrillDownType.matterSummaryDrilldown,
                                                                    'Matters',
                                                                );
                                                            }
                                                        }}
                                                        data={{
                                                            labels: ['Estimate Fees', 'Billed Time & Fees'],
                                                            datasets: [
                                                                {
                                                                    label: 'Cost',
                                                                    ...rvBarDatasetStyle([RV_CHART_COLORS.primary, '#FE9D17']),
                                                                    data: [estimate, billedFee],
                                                                },
                                                            ],
                                                        }}
                                                        options={{
                                                            onHover: this.onHover,
                                                            indexAxis: 'y' as const,
                                                            animation: rvChartAnimation(),
                                                            elements: { bar: { borderWidth: 0 } },
                                                            responsive: true,
                                                            maintainAspectRatio: false,
                                                            scales: {
                                                                x: {
                                                                    grid: rvValueAxisStyle.grid,
                                                                    // beginAtZero: true,
                                                                    suggestedMax: getSuggestedMaxTick([estimate, billedFee]),
                                                                    beginAtZero: true,
                                                                    ticks: {
                                                                        ...rvValueAxisStyle.ticks,
                                                                        maxTicksLimit: getMaxTickLimit([estimate, billedFee]),
                                                                        autoSkip: false,
                                                                        callback: function (this: Scale, tickValue: number, index: number, ticks: Tick[]) {
                                                                            return `${symbol}${formatNumber(tickValue)}`;
                                                                        },
                                                                    },
                                                                },
                                                                y: {
                                                                    grid: rvCategoryAxisStyle.grid,
                                                                    // min: 0,
                                                                    // max: 9 // minimum display
                                                                    ticks: {
                                                                        ...rvCategoryAxisStyle.ticks,
                                                                        autoSkip: false
                                                                    }
                                                                }
                                                            },
                                                            plugins: {
                                                                legend: {
                                                                    position: 'bottom' as const,
                                                                    display: false,
                                                                },
                                                                title: {
                                                                    display: false,
                                                                    text: `Estimate vs Billed Fees`,
                                                                },
                                                                zoom: {
                                                                    pan: {
                                                                        enabled: false,
                                                                        onPan: this.onPan,
                                                                    },
                                                                    zoom: {
                                                                        wheel: {
                                                                            enabled: false,
                                                                        },
                                                                    },
                                                                    // limits: {
                                                                    //   y: { min: 0, max: 9 },
                                                                    // },
                                                                },
                                                                tooltip: {
                                                                    ...rvTooltipStyle,
                                                                    callbacks: {
                                                                        // tslint:disable-next-line: no-any
                                                                        label: function (context: any) {
                                                                            const {
                                                                                //   dataIndex,
                                                                                //   label,
                                                                                formattedValue,
                                                                                //   dataset
                                                                            } = context;

                                                                            return `${symbol}${formattedValue}`;
                                                                        },
                                                                    },
                                                                },
                                                            },
                                                        }}
                                                    />
                                                    {isChartDataEmpty([estimate, billedFee]) && (
                                                        <ChartEmptyState title="No data" hint="Nothing to show for this selection." />
                                                    )}
                                                </BarContainer>
                                                <FooterDescription>
                                                    <ChartStatTiles
                                                        caption="Matters with no estimate"
                                                        tiles={[
                                                            { label: 'Matters', value: formatNumber(mattersWithNoEstimate), color: RV_CHART_COLORS.amber, onClick: () => this.handleNoEstimates() },
                                                            { label: 'Billed fees', value: `${symbol}${formatNumber(mattersWithNoEstimateBilledFees)}`, color: RV_CHART_COLORS.green },
                                                        ]}
                                                    />
                                                    <ChartNotes notes={['Fees only, disbursements are not included']} />
                                                </FooterDescription>
                                            </BarWrapper>
                                        );
                                    }

                                    return (
                                        <LoadWrapper className="load-wrapper">
                                            <RvLoader size="small" />
                                        </LoadWrapper>
                                    );
                                }}
                            </Query>
                        );
                    }}
                </Query>
            </React.Fragment>
        );
    }

    private handleTotalClick(isPercentage: boolean) {
        if (this.props.getSelectedItem) {
            const searchCriterias = new Array<SearchCriteria>();
            if (isPercentage) {
                searchCriterias.push(
                    { property: 'EstimatedCosts', operator: 'gt', comparison: '0' /*, combination: ' OR '*/ },
                    // { property: 'FeesBilled', operator: 'gt', comparison: '0' }
                );
            } else {
                searchCriterias.push({ property: 'EstimatedCosts', operator: 'gt', comparison: '0' });
            }

            this.props.getSelectedItem(searchCriterias, false, 'Estimate vs Billed Fees', DrillDownType.matterSummaryDrilldown, 'Matters');
        }
    }

    private handleNoEstimates() {
        if (this.props.getSelectedItem) {
            const searchCriterias = new Array<SearchCriteria>();

            searchCriterias.push({ property: 'EstimatedCosts', operator: 'eq', comparison: '0' });

            this.props.getSelectedItem(searchCriterias, false, 'Estimate vs Billed Fees', DrillDownType.matterSummaryDrilldown, 'Matters');
        }
    }

    private onCloseChart() {
        if (this.props.onCloseChart) {
            return this.props.onCloseChart('isBilledVsEstimateChartOpen');
        }
    }

    // tslint:disable-next-line: no-any
    private handlePopoverOpen(event: any) {
        this.setAnchorEl(event.currentTarget);
    }

    private handlePopoverClose = () => {
        this.setAnchorEl(null);
    }

    // tslint:disable-next-line: no-any
    private setAnchorEl(anchorElValue: any) {
        this.setState({
            anchorEl: anchorElValue,
            open: Boolean(anchorElValue),
        });
    }

    private onPan = (context: { chart: Chart }) => {
        this.chartRef.current.options.scales.x.min = 0;
        this.chartRef.current.update();
    }

    private onHover(event: ChartEvent, elements: ActiveElement[], chart: Chart) {
        (event?.native?.target as HTMLElement).style.cursor = elements.length > 0 ? 'pointer' : 'auto';
    }
}

// tslint:disable-next-line: no-any
export default withStyles(styles as any, { withTheme: true })(BilledVsEstimateChart as any) as any;

const ChartData = gql`
    query MatterBilledFeeAndEstimateChartData($entityType: Int, $entityGuid: String, $responsibility: Int) {
        statistics {
            matter {
                matterBilledFeeAndEstimate(entityType: $entityType, entityGuid: $entityGuid, responsibility: $responsibility) {
                    billedFee
                    estimate
                    mattersCount
                    mattersWithNoEstimate
                    mattersWithNoEstimateBilledFees
                }
            }
        }
    }
`;

const CurrencySymbol = gql`
    query CurrencySymbol {
        options {
            currencySymbol {
                symbol
            }
        }
    }
`;
